import { convertTimeZone } from "../utils";
import { isCalendarDate, rangoDeAnio, rangoDeMes, type CalendarDate } from "../calendarDate";

/**
 * Núcleo ÚNICO de traducción de filtros de fecha a Prisma.
 *
 * Existía duplicado en los dos traductores de grilla —mrtFilterService (grilla
 * MRT) y filterService (ListStore de cada entidad)— y por eso derivaron: uno
 * usaba `lt` y el otro `lte` en el límite superior, con un off-by-one que hacía
 * que "el día 30" significara cosas distintas según qué grilla mirabas. Ambos
 * importan ahora estas funciones; la lógica riesgosa vive en un solo lugar.
 *
 * Semántica fija para los cuatro: intervalo SEMIABIERTO `[inicio, fin)` con
 * `gte`/`lt`, donde `fin` es el primer instante DESPUÉS del rango elegido. Es la
 * forma correcta para rangos de fecha: no cuenta dos veces la medianoche del
 * borde y no depende de "23:59:59.999".
 */

export type PrismaFilterExpression = Record<string, unknown> | null | undefined;

/** Filtro por día específico (YYYY-MM-DD o DD/MM/YYYY). */
export function dayFilter(
  columnName: string,
  value: string,
  timeZone: string
): PrismaFilterExpression {
  const normalized = value.replace(/\//g, "-");
  const parts = normalized.split("-").map((p) => p.trim());
  if (parts.length !== 3) return null;

  let year: string, month: string, day: string;
  if (parts[0].length === 4) {
    [year, month, day] = parts;
  } else {
    [day, month, year] = parts;
  }

  const fromStr = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  try {
    const from = convertTimeZone(fromStr, timeZone);
    const to = new Date(from);
    to.setDate(from.getDate() + 1);
    return {
      AND: [
        { [columnName]: { gte: from.toISOString() } },
        { [columnName]: { lt: to.toISOString() } },
      ],
    };
  } catch {
    return null;
  }
}

/** Filtro por año (YYYY). */
export function yearFilter(
  columnName: string,
  value: string,
  timeZone: string
): PrismaFilterExpression {
  const year = parseInt(value.trim(), 10);
  if (isNaN(year)) return null;
  try {
    const from = convertTimeZone(`${year}-01-01`, timeZone);
    const to = convertTimeZone(`${year + 1}-01-01`, timeZone);
    return {
      AND: [
        { [columnName]: { gte: from.toISOString() } },
        { [columnName]: { lt: to.toISOString() } },
      ],
    };
  } catch {
    return null;
  }
}

/** Filtro por mes (YYYY-MM o MM/YYYY). */
export function yearMonthFilter(
  columnName: string,
  value: string,
  timeZone: string
): PrismaFilterExpression {
  const parts = value.replace(/\//g, "-").split("-").map((p) => p.trim());
  if (parts.length !== 2) return null;

  let year: number, month: number;
  if (parts[0].length === 4) {
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
  } else {
    month = parseInt(parts[0], 10);
    year = parseInt(parts[1], 10);
  }
  if (isNaN(year) || isNaN(month) || month < 1 || month > 12) return null;

  const fromStr = `${year}-${month.toString().padStart(2, "0")}-01`;
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const toStr = `${nextYear}-${nextMonth.toString().padStart(2, "0")}-01`;
  try {
    const from = convertTimeZone(fromStr, timeZone);
    const to = convertTimeZone(toStr, timeZone);
    return {
      AND: [
        { [columnName]: { gte: from.toISOString() } },
        { [columnName]: { lt: to.toISOString() } },
      ],
    };
  } catch {
    return null;
  }
}

/** Filtro por rango de fechas (desde,hasta). El día "hasta" entra completo. */
export function fromToFilter(
  columnName: string,
  value: string,
  timeZone: string
): PrismaFilterExpression {
  const parts = value.split(",").map((p) => p.trim().replace(/\//g, "-"));
  if (parts.length !== 2) return null;
  const [fromStr, toStr] = parts;

  const normalizeDate = (dateStr: string) =>
    dateStr.split("-").map((s) => s.padStart(2, "0")).join("-");

  try {
    const from = convertTimeZone(normalizeDate(fromStr), timeZone);
    const to = convertTimeZone(normalizeDate(toStr), timeZone);
    to.setDate(to.getDate() + 1); // incluir todo el día final
    return {
      AND: [
        { [columnName]: { gte: from.toISOString() } },
        { [columnName]: { lt: to.toISOString() } },
      ],
    };
  } catch {
    return null;
  }
}

/*
 * Un date (día de calendario, "AAAA-MM-DD"): los mismos cuatro filtros, sin zona ni
 * hora. El server compara días (la columna date de Postgres y el escalar Date de
 * GraphQL, que acepta solo "AAAA-MM-DD"), así que el valor es el texto del día:
 * - un día: equals;
 * - un año o un mes: gte el primer día y lt el primer día del período siguiente;
 * - desde-hasta: gte y lte, los dos días incluidos.
 * Un valor que no es un día que existe no filtra (null), como los de arriba.
 */

/** "AAAA-MM-DD", "AAAA/M/D", "DD-MM-AAAA" o "D/M/AAAA" → el día, si existe. */
function calendarDayOf(value: string): CalendarDate | null {
  const parts = value.trim().replace(/\//g, "-").split("-").map((p) => p.trim());
  if (parts.length !== 3 || !parts.every((p) => /^\d+$/.test(p))) return null;
  const [year, month, day] = parts[0].length === 4 ? parts : [parts[2], parts[1], parts[0]];
  if (year.length !== 4 || month.length > 2 || day.length > 2) return null;
  const candidate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  return isCalendarDate(candidate) ? candidate : null;
}

/** Filtro por día de un date. */
export function calendarDayFilter(columnName: string, value: string): PrismaFilterExpression {
  const day = calendarDayOf(value);
  return day === null ? null : { [columnName]: { equals: day } };
}

/** Filtro por año (AAAA) de un date. */
export function calendarYearFilter(columnName: string, value: string): PrismaFilterExpression {
  const text = value.trim();
  if (!/^\d{4}$/.test(text)) return null;
  try {
    const { gte, lt } = rangoDeAnio(Number(text));
    return { AND: [{ [columnName]: { gte } }, { [columnName]: { lt } }] };
  } catch {
    return null;
  }
}

/** Filtro por mes (AAAA-MM o MM/AAAA) de un date. */
export function calendarYearMonthFilter(columnName: string, value: string): PrismaFilterExpression {
  const parts = value.trim().replace(/\//g, "-").split("-").map((p) => p.trim());
  if (parts.length !== 2 || !parts.every((p) => /^\d+$/.test(p))) return null;
  const [year, month] = parts[0].length === 4 ? parts : [parts[1], parts[0]];
  if (year.length !== 4 || month.length > 2) return null;
  try {
    const { gte, lt } = rangoDeMes(`${year}-${month.padStart(2, "0")}`);
    return { AND: [{ [columnName]: { gte } }, { [columnName]: { lt } }] };
  } catch {
    return null;
  }
}

/** Filtro por rango (desde,hasta) de un date: los dos días incluidos. */
export function calendarFromToFilter(columnName: string, value: string): PrismaFilterExpression {
  const parts = value.split(",");
  if (parts.length !== 2) return null;
  const from = calendarDayOf(parts[0]);
  const to = calendarDayOf(parts[1]);
  if (from === null || to === null) return null;
  return { AND: [{ [columnName]: { gte: from } }, { [columnName]: { lte: to } }] };
}
